import i18n, { type i18n as I18n } from "i18next";
import { initReactI18next } from "react-i18next";
import { localizationConfig } from "./localization-config";
import { getPreferredLocale, setLocaleCookie } from "./locale-cookie";
import type { I18nBundle } from "./resources";
import { ALL_LOCALES, DEFAULT_LOCALE, type Locale } from "./locales";

const SUPPORTED_LOCALES: readonly Locale[] = ALL_LOCALES;

let activeBundle: I18nBundle;

function loadLocaleBundle(lng: string): Promise<void> {
  if (
    lng === DEFAULT_LOCALE ||
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    !SUPPORTED_LOCALES.includes(lng as Locale) ||
    i18n.hasResourceBundle(lng, "translation")
  ) {
    return Promise.resolve();
  }

  return Promise.resolve(activeBundle.loaders[lng]?.() ?? {}).then(
    (translations) => {
      i18n.addResourceBundle(lng, "translation", translations, true, true);
      i18n.emit("languageChanged", lng);
    },
  );
}

/**
 * `options.locale` pins the language, e.g. a storefront's regional `en-GB` for
 * formatting. Only exact catalog locales lazy-load; others fall back to English.
 */
export function initI18n(bundle: I18nBundle, options?: { locale?: string }) {
  activeBundle = bundle;
  const locale = options?.locale ?? getPreferredLocale(SUPPORTED_LOCALES);

  i18n.on("languageChanged", (lng) => {
    if (localizationConfig.isEnabled()) {
      setLocaleCookie(lng);
    }
    void loadLocaleBundle(lng);
  });

  void i18n
    .use(initReactI18next)
    .init({
      resources: { [DEFAULT_LOCALE]: { translation: bundle.en } },
      lng: locale,
      fallbackLng: DEFAULT_LOCALE,
      keySeparator: false,
      nsSeparator: false,
      contextSeparator: "__rc__",
      pluralSeparator: "__rp__",
      interpolation: { escapeValue: false },
      react: { useSuspense: false },
      returnEmptyString: false,
      // init otherwise logs a vendor notice, including on merchant storefronts.
      showSupportNotice: false,
    })
    .then(() => loadLocaleBundle(locale));
}

const latestLocaleRequest = new WeakMap<I18n, number>();

/**
 * Loads the locale bundle before switching to avoid an English flash. The
 * latest request wins, so a slow bundle cannot undo a newer switch.
 */
export async function setLocale(
  locale: Locale,
  instance: I18n = i18n,
): Promise<void> {
  const request = (latestLocaleRequest.get(instance) ?? 0) + 1;
  latestLocaleRequest.set(instance, request);
  if (
    locale !== DEFAULT_LOCALE &&
    !instance.hasResourceBundle(locale, "translation")
  ) {
    const translations = (await activeBundle.loaders[locale]?.()) ?? {};
    instance.addResourceBundle(
      locale,
      "translation",
      translations,
      true,
      true,
    );
  }
  if (latestLocaleRequest.get(instance) !== request) {
    return;
  }
  await instance.changeLanguage(locale);
}

export function updateLocalization(
  enabled: boolean,
  preferredLocale?: string | null,
): void {
  localizationConfig.setEnabled(enabled);
  if (!i18n.isInitialized) {
    return;
  }
  const locale = getPreferredLocale(SUPPORTED_LOCALES, preferredLocale);
  if (
    locale !== DEFAULT_LOCALE &&
    !i18n.hasResourceBundle(locale, "translation")
  ) {
    void setLocale(locale);
    return;
  }
  if (i18n.language !== locale) {
    void setLocale(locale);
  }
}
